import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react'
import { AUTH_CHANGED_EVENT, resolveApiPath } from './serverContext'
import { createPtyJobConnector } from './ptyJob'
import { createAiSessionJobConnector } from './aiSessionJob'
import { createDeploymentHistoryConnector } from './deploymentJob'
import { createAgentOperationConnector } from './agentOperationJob'
import { createAiInstallationJobConnector } from './aiInstallationJob'
import { getTenantStorageScope, tenantStorageKey } from './tenantStorage'
import { getAuthToken } from './tokenStorage'
import { notificationTimestamp } from './notificationTime'
import NotificationToast from '@/components/NotificationToast'
import { notificationActions, type NotificationActions } from './notificationActions'
import { playNotificationSound } from './notificationSound'
import { pendingManagementNotice } from './pendingManagementNotice'
import { openNotificationTarget } from './notificationNavigation'
import {
  getDeviceNotificationRouting,
  notificationToneForEvent,
  notificationCategoryForEvent,
  notificationPresentation,
  syncNotificationRoutingToServiceWorker,
  type NotificationCategory,
  type NotificationTone,
} from './operationNotifications'

export interface JobResume {
  /** Yenileme sonrası güvenle yeniden bağlanabilen uzun ömürlü iş türü. */
  kind: 'pty' | 'ai-session' | 'deployment-history' | 'agent-operation' | 'ai-installation'
  operationId?: string
  serverName?: string
  basePath?: string
  startPath?: 'commands' | 'sessions'
  payload?: Record<string, unknown>
  aiSessionId?: string
  sessionId?: string
  siteId?: string
  startedAt?: number
  deploymentKind?: 'deploy' | 'rollback' | 'promote' | 'sync-push'
  /** Agent üzerinde kalıcı çalışan paket/bakım işinin salt okunur durum yolu. */
  statusPath?: string
  /** PTY çıktısının sunucu buffer'ındaki göreli konumu. Yenilemede eski
   * scrollback'i tekrar yazıp çift göstermemek için kullanılır. */
  cursor?: number
}

export interface Job {
  id: string
  title: string
  lines: string[]
  status: 'running' | 'done' | 'error' | 'deferred'
  startedAt: number
  finishedAt: number | null
  /** true ise JobsWidget çift yönlü bir terminal (InteractiveTerminal) render eder. */
  interactive?: boolean
  // Which virtual key set the interactive terminal should offer.
  keys?: 'shell' | 'ai-cli'
  /** İşin sayfa yenileme sonrası yeniden bağlanabilmesi için metadata. */
  resume?: JobResume
}

export interface JobHandlers {
  appendLine: (line: string) => void
  /** Son log satırını, ilerleme sayaçları gibi canlı durumlar için günceller. */
  replaceLastLine: (line: string) => void
  finish: (ok: boolean, notification?: JobNotificationResult) => void
  /** Connector'ın ürettiği session ID'yi kalıcı job kaydına yazar. */
  setResume: (resume: JobResume) => void
}

export interface JobNotificationResult {
  deferred?: boolean
  notify?: boolean
  body?: string
  url?: string
  eventKey?: string
  category?: NotificationCategory
}

/** İnteraktif (çift yönlü) bir connector'ın döndürebileceği kontroller. */
export interface JobControls {
  cleanup?: () => void
  sendInput?: (data: string) => void
  resize?: (rows: number, cols: number) => void
}

/**
 * Özel taşıyıcı (ör. WebSocket + polling fallback, ya da bir PTY oturumu).
 * Geriye dönük uyumluluk: düz bir `cleanup` fonksiyonu döndürebilir (eski
 * davranış) VEYA `sendInput`/`resize` de içeren bir `JobControls` nesnesi
 * döndürebilir (interaktif terminal işleri için).
 */
export type JobConnector = (handlers: JobHandlers) => (() => void) | JobControls | void

interface JobsContextValue {
  jobs: Job[]
  expandedId: string | null
  /**
   * source bir URL string ise dahili SSE runner (POST) kullanılır; fonksiyon
   * ise özel bir connector (ör. WebSocket, PTY) olarak çalıştırılır. init
   * verilirse (ör. JSON body/header gerektiren endpoint'ler için) fetch'e
   * birleştirilir. opts.interactive true ise iş çift yönlü (girdi/resize
   * destekli) bir terminal olarak render edilir.
   */
  startJob: (title: string, source: string | JobConnector, init?: RequestInit, opts?: { interactive?: boolean; keys?: 'shell' | 'ai-cli'; resume?: JobResume }) => string
  expand: (id: string) => void
  minimize: () => void
  remove: (id: string) => void
  sendInput: (id: string, data: string) => void
  resize: (id: string, rows: number, cols: number) => void
  stopJob: (id: string) => void
}

const JobsContext = createContext<JobsContextValue | null>(null)
const JOB_STORAGE_KEY = 'scpPersistentJobs'
const JOB_EXPANDED_KEY = 'scpPersistentJobsExpanded'

function persistJobsSnapshot(jobs: Job[], scope = getTenantStorageScope()) {
  try {
    // Her iş için sınırlı bir kuyruk tutuyoruz; böylece uzun süren işlerde
    // localStorage kotası büyümez ama yenileme anındaki son çıktı korunur.
    const compact = jobs.slice(-10).map(job => ({
      ...job,
      lines: job.lines.slice(-300).map(line => line.slice(0, 1200)),
    }))
    localStorage.setItem(`${JOB_STORAGE_KEY}:${scope}`, JSON.stringify(compact))
  } catch {
    // Depolama kotası doluysa canlı işi etkileme.
  }
}

function restoredJobs(scope = getTenantStorageScope()): Job[] {
  try {
    const value = JSON.parse(localStorage.getItem(`${JOB_STORAGE_KEY}:${scope}`) || '[]')
    if (!Array.isArray(value)) return []
    return value.slice(-10).map((job: Job) => {
      const lines = Array.isArray(job.lines) ? job.lines : []
      const resumable = ['running', 'deferred'].includes(job.status) && (
        (job.resume?.kind === 'pty' && !!job.resume.sessionId) ||
        (job.resume?.kind === 'ai-session' && !!job.resume.aiSessionId) ||
        (job.resume?.kind === 'deployment-history' && !!job.resume.siteId && !!job.resume.startedAt) ||
        (job.resume?.kind === 'agent-operation' && !!job.resume.statusPath) ||
        (job.resume?.kind === 'ai-installation' && !!job.resume.statusPath && !!job.resume.operationId)
      )
      // PTY session ID'si varsa aşağıdaki provider effect'i aynı sürece
      // yeniden bağlanacak. Diğer POST/SSE işlerini yenilemede tekrar
      // çalıştırmak güvenli olmadığı için yalnız logunu koruyoruz.
      return job.status === 'running' && !resumable ? {
        ...job,
        status: 'error' as const,
        finishedAt: Date.now(),
        lines: [...lines, 'Sayfa yeniden yüklendi; canlı iş bağlantısı kesildi. Mevcut işlem durumu sunucuda ayrıca kontrol edilebilir.'],
      } : { ...job, lines }
    })
  } catch { return [] }
}

function requestNotifyPermission() {
  if (typeof Notification === 'undefined') return
  if (Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {})
  }
}

function notify(title: string, body: string, url = '/notifications/history') {
  const nativeNotify = (window as any).SCPNative?.notify
  if (typeof nativeNotify === 'function') {
    void Promise.resolve(nativeNotify(title, body, undefined, url, { url })).catch(() => {})
    return
  }
  if (typeof Notification === 'undefined') return
  if (Notification.permission !== 'granted') return
  try {
    new Notification(title, { body, icon: '/favicon.ico' })
  } catch {
    // sessizce yut — bazı tarayıcı/izin kombinasyonlarında Notification() atabilir
  }
}

async function runSSEJob(path: string, isCancelled: () => boolean, handlers: JobHandlers, init?: RequestInit) {
  const { appendLine, finish } = handlers
  const token = getAuthToken()
  try {
    const response = await fetch(resolveApiPath(path), {
      method: 'POST',
      ...init,
      headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream', ...(init?.headers || {}) },
    })
    if (!response.ok || !response.body) {
      const err = await response.json().catch(() => ({}))
      appendLine((err as any)?.detail || `HTTP ${response.status}`)
      finish(false)
      return
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let sawError = false
    let sawDone = false
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (isCancelled()) { reader.cancel().catch(() => {}); break }
      buffer += decoder.decode(value, { stream: true })
      const parts = buffer.split('\n\n')
      buffer = parts.pop() ?? ''
      for (const part of parts) {
        const line = part.trim()
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '__DONE__') {
          sawDone = true
        } else if (data.startsWith('__ERROR__')) {
          sawError = true
          appendLine(data)
        } else {
          appendLine(data)
        }
      }
    }
    if (!sawDone && !sawError && !isCancelled()) appendLine('İşlem tamamlanma cevabı gelmeden bağlantı kapandı.')
    finish(sawDone && !sawError)
  } catch (e: any) {
    appendLine(String(e))
    finish(false)
  }
}

export function JobsProvider({ children }: { children: ReactNode }) {
  const [storageScope, setStorageScope] = useState(getTenantStorageScope)
  const [jobs, setJobs] = useState<Job[]>(() => restoredJobs(storageScope))
  const [expandedId, setExpandedId] = useState<string | null>(() => {
    try {
      const id = localStorage.getItem(`${JOB_EXPANDED_KEY}:${storageScope}`)
      const available = restoredJobs(storageScope)
      return id && available.some(job => job.id === id) ? id : null
    } catch { return null }
  })
  const [operationNotice, setOperationNotice] = useState<{
    id: string; createdAt: number; eventTime: boolean; jobId?: string; title: string; body: string; tone: NotificationTone; url?: string; category: NotificationCategory; approval?: boolean; actions?: NotificationActions
  } | null>(null)
  const cancelRefs = useRef<Record<string, boolean>>({})
  const cleanupRefs = useRef<Record<string, () => void>>({})
  const controlsRefs = useRef<Record<string, JobControls>>({})
  const restoredConnectionsRef = useRef(false)
  const operationNoticeRef = useRef(operationNotice)
  const seenManagementApprovalsRef = useRef(new Set<string>())
  const jobsRef = useRef(jobs)
  const storageScopeRef = useRef(storageScope)
  jobsRef.current = jobs
  storageScopeRef.current = storageScope
  operationNoticeRef.current = operationNotice

  useEffect(() => {
    persistJobsSnapshot(jobs, storageScope)
  }, [jobs, storageScope])

  useEffect(() => { void syncNotificationRoutingToServiceWorker() }, [storageScope])

  // Kart hangi yoldan gelirse gelsin (canlı iş sonucu, push köprüsü, service
  // worker) sesi tek yerden çal; kullanıcının seçtiği ses `Sessiz` ise veya
  // otomatik oynatma engelliyse kart yine de gösterilir.
  useEffect(() => {
    if (operationNotice) playNotificationSound()
  }, [operationNotice?.id])

  useEffect(() => {
    const receive = (event: Event) => {
      const payload = (event as CustomEvent<Record<string, unknown>>).detail || {}
      const title = String(payload.title || 'ServerNox')
      const body = String(payload.body || 'Yeni bildirim')
      const eventKey = String(payload.event || '')
      const category = notificationCategoryForEvent(eventKey)
      if (eventKey === 'ai.permission_required' && typeof payload.operation_id === 'string') {
        seenManagementApprovalsRef.current.add(payload.operation_id)
      }
      setOperationNotice({
        id: String(payload.id || `${eventKey}:${Date.now()}`), title, body,
        ...notificationTimestamp(payload.created_at),
        tone: notificationToneForEvent(eventKey),
        approval: eventKey === 'ai.permission_required',
        actions: notificationActions(payload),
        url: typeof payload.url === 'string' ? payload.url : '/notifications/history',
        category,
      })
    }
    window.addEventListener('servernox-in-app-notification', receive)
    return () => window.removeEventListener('servernox-in-app-notification', receive)
  }, [])

  // AI Coding kendi ayrıntılı onay panelini korur. Diğer sayfalarda ise push
  // izni olmasa bile yalnız güvenli özet ve inceleme bağlantısı gösterilir.
  useEffect(() => {
    let stopped = false
    let timer: number | undefined
    const poll = async () => {
      if (stopped) return
      try {
        const token = getAuthToken()
        if (document.visibilityState === 'visible' && window.location.pathname !== '/ai-coding' && token) {
          const response = await fetch(resolveApiPath('/ai-management/pending'), {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
          })
          if (response.ok) {
            const payload = await response.json().catch(() => ({}))
            const items: unknown[] = Array.isArray(payload?.items) ? payload.items : []
            const notice = items.map(pendingManagementNotice)
              .find(item => item && !seenManagementApprovalsRef.current.has(item.id))
            if (notice && !operationNoticeRef.current) {
              seenManagementApprovalsRef.current.add(notice.id)
              setOperationNotice({
                id: `management:${notice.id}`,
                ...notificationTimestamp(undefined),
                title: notice.title,
                body: notice.body,
                tone: 'warning',
                url: notice.url,
                category: 'ai',
                approval: true,
              })
            }
          }
        }
      } catch {
        // Bildirim sorgusu sayfa kullanımını hiçbir zaman engellememeli.
      } finally {
        if (!stopped) timer = window.setTimeout(poll, 8000)
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && timer) {
        window.clearTimeout(timer)
        void poll()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    void poll()
    return () => {
      stopped = true
      if (timer) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [storageScope])

  // AuthProvider uygulamanın üstünde kaldığı için hesap veya
  // organizasyon değişiminde bu provider unmount olmaz. Eski tenant'ın
  // terminal loglarını bellekte de tutmayıp yeni kapsamdaki işleri yükle.
  useEffect(() => {
    const onAuthChanged = () => {
      setOperationNotice(null)
      seenManagementApprovalsRef.current.clear()
      Object.keys(cancelRefs.current).forEach(id => { cancelRefs.current[id] = true; cleanupRefs.current[id]?.() })
      cancelRefs.current = {}
      cleanupRefs.current = {}
      controlsRefs.current = {}
      const nextScope = getTenantStorageScope()
      const nextJobs = restoredJobs(nextScope)
      const expanded = localStorage.getItem(`${JOB_EXPANDED_KEY}:${nextScope}`)
      storageScopeRef.current = nextScope
      restoredConnectionsRef.current = false
      setStorageScope(nextScope)
      setJobs(nextJobs)
      setExpandedId(expanded && nextJobs.some(job => job.id === expanded) ? expanded : null)
    }
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
  }, [])

  // React effect'inin çalışmasına fırsat kalmadan gelen yenileme/kapatma
  // durumlarında da eldeki son state'i senkron olarak diske yaz.
  useEffect(() => {
    const persistLatest = () => persistJobsSnapshot(jobsRef.current, storageScopeRef.current)
    window.addEventListener('pagehide', persistLatest)
    window.addEventListener('beforeunload', persistLatest)
    return () => {
      window.removeEventListener('pagehide', persistLatest)
      window.removeEventListener('beforeunload', persistLatest)
    }
  }, [])

  const connectJob = useCallback((id: string, title: string, source: string | JobConnector, init?: RequestInit, resume?: JobResume) => {
    cancelRefs.current[id] = false
    const startedScope = storageScopeRef.current
    const current = () => storageScopeRef.current === startedScope && cancelRefs.current[id] !== true
    const appendLine = (line: string) => {
      if (!current()) return
      setJobs(prev => prev.map(j => j.id === id ? { ...j, lines: [...j.lines, line] } : j))
    }
    const replaceLastLine = (line: string) => {
      if (!current()) return
      setJobs(prev => prev.map(j => {
        if (j.id !== id || j.lines.length === 0) return j
        return { ...j, lines: [...j.lines.slice(0, -1), line] }
      }))
    }
    const setResume = (resume: JobResume) => {
      if (!current()) return
      setJobs(prev => prev.map(j => j.id === id ? { ...j, resume } : j))
    }
    const finish = (ok: boolean, notification: JobNotificationResult = {}) => {
      if (!current()) return
      setJobs(prev => prev.map(j => j.id === id ? { ...j, status: notification.deferred ? 'deferred' : ok ? 'done' : 'error', finishedAt: notification.deferred ? null : Date.now() } : j))
      // AI completion is delivered once by the server, including background push.
      if (resume?.kind === 'ai-session' || notification.notify === false) return
      const tone: NotificationTone = notification.deferred ? 'warning' : ok ? 'success' : 'error'
      const category = notification.category || 'operations'
      const routing = getDeviceNotificationRouting()
      const foreground = document.visibilityState === 'visible'
      const presentation = notificationPresentation(routing, category, foreground)
      const body = notification.body || (category === 'ai'
        ? (ok ? 'AI yanıtı hazır. Sohbeti açmak için dokunun.' : 'AI çalışması tamamlanamadı; sohbet ayrıntılarını inceleyin.')
        : (ok ? 'İşlem başarıyla tamamlandı.' : 'İşlem hata ile sonuçlandı.'))
      const url = notification.url || ''
      if (presentation.inApp) setOperationNotice({ id, createdAt: Date.now(), eventTime: true, jobId: id, title, body, tone, url, category })
      if (presentation.system) {
        notify(
          notification.deferred ? `⏸ ${title}` : ok ? `✓ ${title}` : `✗ ${title}`,
          body,
          url || '/notifications/history',
        )
      }
    }
    const handlers = { appendLine, replaceLastLine, finish, setResume }
    if (typeof source === 'function') {
      const result = source(handlers)
      if (typeof result === 'function') {
        cleanupRefs.current[id] = result
      } else if (result) {
        controlsRefs.current[id] = result
        if (result.cleanup) cleanupRefs.current[id] = result.cleanup
      }
      return
    }
    runSSEJob(source, () => cancelRefs.current[id], handlers, init)
  }, [])

  const startJob = useCallback((title: string, source: string | JobConnector, init?: RequestInit, opts?: { interactive?: boolean; keys?: 'shell' | 'ai-cli'; resume?: JobResume }): string => {
    const category: NotificationCategory = opts?.resume?.kind === 'ai-session' ? 'ai' : 'operations'
    const routing = getDeviceNotificationRouting()
    if (notificationPresentation(routing, category, document.visibilityState === 'visible').system || routing.backgroundSystem[category]) requestNotifyPermission()
    const id = Math.random().toString(36).slice(2, 10)
    const job: Job = { id, title, lines: [], status: 'running', startedAt: Date.now(), finishedAt: null, interactive: opts?.interactive, keys: opts?.keys, resume: opts?.resume }
    setJobs(prev => [...prev, job])
    setExpandedId(id)
    connectJob(id, title, source, init, opts?.resume)
    return id
  }, [connectJob])

  // Sayfa yenilemesi sonrası gerçek PTY süreçlerine aynı session ID ile
  // yeniden bağlan. Generic SSE işleri tekrar POST edilmez; logları korunur.
  useEffect(() => {
    if (restoredConnectionsRef.current) return
    restoredConnectionsRef.current = true
    jobs.filter(job => ['running', 'deferred'].includes(job.status) && (
      (job.resume?.kind === 'pty' && !!job.resume.sessionId) ||
      (job.resume?.kind === 'ai-session' && !!job.resume.aiSessionId) ||
      (job.resume?.kind === 'deployment-history' && !!job.resume.siteId && !!job.resume.startedAt) ||
      (job.resume?.kind === 'agent-operation' && !!job.resume.statusPath) ||
        (job.resume?.kind === 'ai-installation' && !!job.resume.statusPath && !!job.resume.operationId)
    )).forEach(job => {
      const resume = job.resume!
      if (resume.kind === 'ai-installation' && resume.statusPath && resume.operationId) {
        connectJob(job.id, job.title, createAiInstallationJobConnector(resume), undefined, resume)
      } else if (resume.kind === 'deployment-history' && resume.siteId) {
        connectJob(job.id, job.title, createDeploymentHistoryConnector(resume), undefined, resume)
      } else if (resume.kind === 'ai-session' && resume.aiSessionId) {
        connectJob(job.id, job.title, createAiSessionJobConnector(resume.aiSessionId, resume), undefined, resume)
      } else if (resume.kind === 'agent-operation' && resume.statusPath) {
        connectJob(job.id, job.title, createAgentOperationConnector(resume.statusPath, resume), undefined, resume)
      } else if (resume.kind === 'pty' && resume.basePath && resume.startPath) {
        connectJob(job.id, job.title, createPtyJobConnector(resume.basePath, resume.startPath, resume.payload || {}, {
          existingSessionId: resume.sessionId,
          resume,
        }), undefined, resume)
      }
    })
  }, [jobs, connectJob])

  const expand = useCallback((id: string) => setExpandedId(id), [])
  const minimize = useCallback(() => setExpandedId(null), [])
  const remove = useCallback((id: string) => {
    setJobs(prev => {
      const job = prev.find(j => j.id === id)
      if (!job || (job.status === 'running' && job.resume?.kind !== 'ai-session')) return prev
      cancelRefs.current[id] = true
      cleanupRefs.current[id]?.()
      delete cleanupRefs.current[id]
      delete controlsRefs.current[id]
      delete cancelRefs.current[id]
      return prev.filter(j => j.id !== id)
    })
    setExpandedId(prev => prev === id ? null : prev)
  }, [])
  /**
   * `remove()`'un aksine ÇALIŞAN bir işi de durdurabilir — interaktif
   * terminaller için "durdur" düğmesi bunu kullanır (ör. takılmış bir komut).
   * Bağlayıcının cleanup'ını çağırır ve işi hemen 'error' olarak işaretler;
   * kayıt listede kalır (kullanıcı isterse sonra remove() ile silebilir).
   */
  const stopJob = useCallback((id: string) => {
    cancelRefs.current[id] = true
    controlsRefs.current[id]?.cleanup?.()
    cleanupRefs.current[id]?.()
    delete cleanupRefs.current[id]
    delete controlsRefs.current[id]
    setJobs(prev => prev.map(j => j.id === id && j.status === 'running' ? { ...j, status: 'error', finishedAt: Date.now() } : j))
  }, [])
  const sendInput = useCallback((id: string, data: string) => {
    controlsRefs.current[id]?.sendInput?.(data)
  }, [])
  const resize = useCallback((id: string, rows: number, cols: number) => {
    controlsRefs.current[id]?.resize?.(rows, cols)
  }, [])

  const dismissNotice = useCallback(() => setOperationNotice(null), [])

  useEffect(() => {
    try {
      const key = tenantStorageKey(JOB_EXPANDED_KEY)
      if (expandedId) localStorage.setItem(key, expandedId)
      else localStorage.removeItem(key)
    } catch {}
  }, [expandedId, storageScope])

  return (
    <JobsContext.Provider value={{ jobs, expandedId, startJob, expand, minimize, remove, sendInput, resize, stopJob }}>
      {children}
      {operationNotice && (
        <NotificationToast createdAt={operationNotice.createdAt} eventTime={operationNotice.eventTime} key={operationNotice.id} title={operationNotice.title} body={operationNotice.body} tone={operationNotice.tone} hasJob={!!operationNotice.jobId} approval={operationNotice.approval} actions={operationNotice.actions} onDismiss={dismissNotice} onOpen={() => {
          if (operationNotice.jobId) setExpandedId(operationNotice.jobId)
          else openNotificationTarget(operationNotice.url)
          setOperationNotice(null)
        }} />
      )}
    </JobsContext.Provider>
  )
}

export function useJobs(): JobsContextValue {
  const ctx = useContext(JobsContext)
  if (!ctx) throw new Error('useJobs, JobsProvider içinde kullanılmalı')
  return ctx
}
